import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useContext, useRef } from 'preact/hooks';
import { SDKContext } from '../context';
import { PLATFORM_MESSAGES } from '../constants';
import { useIsMouMobile } from '../utils/responsive';
import { useCommercialAnimation } from '../utils/animations';
import { OfferModal } from './OfferModal';
import { OfferHeader } from './OfferHeader';
import { OfferGrid } from './OfferGrid';
import { OfferFooter } from './OfferFooter';
import { OfferwallCTA } from './OfferwallCTA';
import { injectMacrosText } from '../utils';
import { getShowDelayMs } from '../utils/sdkRunTime';
import { openPublisherOfferwall } from '../utils/offerwall';
import '../styles/MOU.css';
/**
 * A Preact component that displays a MOU (Multi Offer Unit) modal ad.
 */
export function MultiOfferUnit() {
    const ctx = useContext(SDKContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [offers, setOffers] = useState([]);
    const [settings, setSettings] = useState(null);
    const [styles, setStyles] = useState(null);
    const [userAttributes, setUserAttributes] = useState(ctx?.sdk.userAttributes ?? {});
    const [, setEmbeddedSettings] = useState({
        enabled: false,
        showHeader: true,
        showFooter: true,
        showBorder: true,
        targetElement: 'adpx',
    });
    const isModalOpenRef = useRef(false);
    const showDelayTimerRef = useRef(null);
    const sessionCompletedRef = useRef(false);
    isModalOpenRef.current = isModalOpen;
    useEffect(() => {
        if (!ctx)
            return;
        const { sdk, store } = ctx;
        const clearShowDelayTimer = () => {
            if (showDelayTimerRef.current != null) {
                clearTimeout(showDelayTimerRef.current);
                showDelayTimerRef.current = null;
            }
        };
        const scheduleModal = (storeSettings) => {
            const delayMs = getShowDelayMs(storeSettings);
            clearShowDelayTimer();
            if (delayMs === 0) {
                sessionCompletedRef.current = false;
                setIsModalOpen(true);
                return;
            }
            setIsModalOpen(false);
            showDelayTimerRef.current = setTimeout(() => {
                showDelayTimerRef.current = null;
                sessionCompletedRef.current = false;
                setIsModalOpen(true);
            }, delayMs);
        };
        const handleStoreChange = () => {
            const storeOffers = store.getOffers();
            const storeSettings = store.getSettings();
            const storeStyles = store.getStyles();
            setOffers(storeOffers);
            setSettings(storeSettings);
            setStyles(storeStyles);
            if (storeSettings?.embedded) {
                setEmbeddedSettings(prev => ({
                    ...prev,
                    ...storeSettings.embedded,
                }));
            }
            const ready = storeOffers.length > 0 && storeSettings != null && storeStyles != null;
            if (!ready) {
                clearShowDelayTimer();
                setIsModalOpen(false);
                return;
            }
            if (isModalOpenRef.current) {
                clearShowDelayTimer();
                return;
            }
            if (!sdk.autoShow) {
                return;
            }
            scheduleModal(storeSettings);
        };
        const handleShow = () => {
            if (isModalOpenRef.current)
                return;
            const storeOffers = store.getOffers();
            const storeSettings = store.getSettings();
            const storeStyles = store.getStyles();
            const ready = storeOffers.length > 0 && storeSettings != null && storeStyles != null;
            if (ready) {
                scheduleModal(storeSettings);
            }
        };
        store.on('change', handleStoreChange);
        store.on('offers-updated', handleStoreChange);
        store.on('show', handleShow);
        handleStoreChange();
        setUserAttributes(sdk.userAttributes);
        return () => {
            clearShowDelayTimer();
            store.off('change', handleStoreChange);
            store.off('offers-updated', handleStoreChange);
            store.off('show', handleShow);
        };
    }, [ctx]);
    const isMobile = useIsMouMobile();
    // Commercial animation for professional offer transitions
    useCommercialAnimation(0); // MOU doesn't change index
    const completeUspSession = () => {
        if (!ctx || sessionCompletedRef.current)
            return;
        const { sdk } = ctx;
        const storeSettings = sdk.getStore().getSettings();
        const apiPub = sdk.apiPayload?.pub_user_id;
        if (storeSettings?.enable_user_selected_perks &&
            storeSettings?.enable_usp &&
            apiPub) {
            sdk.completeSession();
            sessionCompletedRef.current = true;
        }
    };
    const handleClose = () => {
        completeUspSession();
        ctx?.sdk.sendMessage(PLATFORM_MESSAGES.CLOSE_MODAL.name, {
            ...PLATFORM_MESSAGES.CLOSE_MODAL,
            timestamp: Date.now(),
        });
        setIsModalOpen(false);
    };
    const handleOfferClick = (offer) => {
        if (offer.click_url) {
            window.open(offer.click_url, '_blank');
        }
        ctx?.sdk.sendMessage(PLATFORM_MESSAGES.AD_TAKEN.name, {
            ...PLATFORM_MESSAGES.AD_TAKEN,
            ad_id: offer.id,
            target_url: offer.click_url,
        });
        ctx?.sdk.sendMessage(PLATFORM_MESSAGES.URL_CLICKED.name, {
            ...PLATFORM_MESSAGES.URL_CLICKED,
            target_url: offer.click_url,
            external: settings?.open_overlay_offerwall !== true,
        });
    };
    // Register handleClose callback with store
    useEffect(() => {
        if (!ctx)
            return;
        const { store } = ctx;
        store.setModalCloseCallback(handleClose);
        return () => store.clearModalCloseCallback();
    }, [ctx]);
    if (!offers.length || !settings || !styles) {
        return _jsx("div", {});
    }
    const apiPayload = {
        ...userAttributes,
        sessionId: ctx?.store.getSessionId(),
        accountId: ctx?.sdk.accountId,
        awinAdvertiserId: ctx?.sdk.awinAdvertiserId,
        campaignId: ctx?.sdk.campaignId,
        pub_user_id: userAttributes.pub_user_id,
        adpx_fp: ctx?.store.getFingerprint() ?? undefined,
    };
    // Check if USP is enabled
    const isUSPEnabled = settings.enable_user_selected_perks &&
        settings.enable_usp &&
        apiPayload?.pub_user_id;
    // Check if offerwall should be shown
    const shouldShowOfferwall = settings.enable_offerwall && settings.offerwall_show_cta;
    const shouldShowOfferwallInLeadIn = shouldShowOfferwall && !isUSPEnabled;
    const handleHeaderClose = () => {
        if (settings.enable_offerwall && settings.open_offerwall && ctx) {
            completeUspSession();
            const root = ctx.store.getShadowRoot() ?? document;
            openPublisherOfferwall({
                settings,
                styles,
                apiPayload,
                root,
                fromCloseButton: true,
                isWebView: ctx.sdk.isWebView,
                sendMessage: (n, d) => ctx.sdk.sendMessage(n, d),
                closeModal: () => {
                    void ctx.store.closeModal();
                },
            });
            return;
        }
        handleClose();
    };
    return (_jsx(OfferModal, { isOpen: isModalOpen, onClose: handleClose, settings: settings, styles: styles, children: _jsxs("div", { id: 'adpx_mou-content-wrapper', style: isMobile
                ? {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }
                : {}, children: [_jsx(OfferHeader, { settings: settings, styles: styles, userAttributes: userAttributes, isFirstOffer: true, onClose: handleHeaderClose }), (styles.header?.lead_in_text || shouldShowOfferwallInLeadIn) && (_jsxs("div", { id: 'adpx_mou-lead-in-container', style: {
                        padding: '8px 0px',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: 'max-content',
                        margin: !isMobile ? '0 34px' : '0 24px',
                    }, children: [styles.header?.lead_in_text && (_jsx("p", { id: 'adpx_mou-lead-in', style: {
                                fontSize: `${styles.header?.headLineAndLeadInFontSize || 14}px`,
                                color: styles.header?.lead_in_text_color ||
                                    styles.header?.textColor ||
                                    '#666',
                                fontFamily: styles.offerText?.font || 'Roboto, sans-serif',
                                margin: '0',
                                lineHeight: '1.4',
                                fontWeight: 'normal',
                                justifyContent: styles.header?.lead_in_alignment || 'start',
                            }, children: injectMacrosText(styles.header.lead_in_text, userAttributes) })), shouldShowOfferwallInLeadIn && !isMobile && (_jsx("div", { id: 'adpx_mou-offerwall-cta-container', children: _jsx(OfferwallCTA, { settings: settings, styles: styles, apiPayload: apiPayload }) }))] })), _jsx("div", { id: 'adpx_mou-scroll-container', style: isMobile
                        ? {
                            flex: 1,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            minHeight: '160px',
                            maxHeight: '360px',
                            top: '0',
                            position: 'relative',
                            justifyContent: 'flex-start',
                        }
                        : {}, children: _jsx(OfferGrid, { offers: offers, settings: settings, styles: styles, apiPayload: apiPayload, onOfferClick: handleOfferClick }) }), shouldShowOfferwallInLeadIn && isMobile && (_jsx("div", { id: 'adpx_mou-offerwall-cta-container-mobile', style: {
                        padding: '12px 24px',
                        textAlign: 'center',
                        width: 'auto',
                    }, children: _jsx(OfferwallCTA, { settings: settings, styles: styles, apiPayload: apiPayload }) })), _jsx(OfferFooter, { settings: settings, styles: styles })] }) }));
}
